import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Edit, Loader2, RefreshCw, Eye, Plus, AlertTriangle } from 'lucide-react';
import { EmailHtmlPreview } from '../ui/email-html-preview';
import { apiService, EventEmailTemplate } from '../../services/api';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import { extractErrorMessage } from '../../utils/errorHandler';



const USAGE_WARNING_VARIABLES = [
  '{{appName}}',
  '{{userName}}',
  '{{customerName}}',
  '{{resourceName}}',
  '{{usedAmount}}',
  '{{limitAmount}}',
  '{{percentage}}',
  '{{upgradeUrl}}',
];

const TEMPLATE_VARIABLES: Record<string, string[]> = {
  usage_warning_50: USAGE_WARNING_VARIABLES,
  usage_warning_70: USAGE_WARNING_VARIABLES,
  usage_warning_80: USAGE_WARNING_VARIABLES,
  usage_warning_90: USAGE_WARNING_VARIABLES,
  usage_limit_reached: [
    '{{appName}}',
    '{{userName}}',
    '{{customerName}}',
    '{{resourceName}}',
    '{{upgradeUrl}}',
  ],
  admin_event_notification: [
    '{{appName}}',
    '{{adminName}}',
    '{{eventLabel}}',
    '{{customerName}}',
    '{{description}}',
    '{{eventTime}}',
  ],
  customer_suspended: [
    '{{appName}}',
    '{{userName}}',
    '{{customerName}}',
    '{{reason}}',
  ],
  customer_resumed: [
    '{{appName}}',
    '{{userName}}',
    '{{customerName}}',
  ],
  phone_number_purchased: [
    '{{appName}}',
    '{{userName}}',
    '{{customerName}}',
    '{{phoneNumber}}',
    '{{agentName}}',
    '{{date}}',
  ],
  user_invitation: [
    '{{appName}}',
    '{{inviteeName}}',
    '{{inviterName}}',
    '{{customerName}}',
    '{{invitationUrl}}',
    '{{expiresAt}}',
  ],
  invitation_accepted: [
    '{{appName}}',
    '{{inviterName}}',
    '{{inviteeName}}',
    '{{inviteeEmail}}',
    '{{customerName}}',
  ],
  invitation_revoked: [
    '{{appName}}',
    '{{inviteeName}}',
    '{{customerName}}',
  ],
  registration_verification: [
    '{{appName}}',
    '{{name}}',
    '{{companyName}}',
    '{{verificationUrl}}',
    '{{expiresAt}}',
  ],
  registration_welcome: [
    '{{appName}}',
    '{{name}}',
    '{{companyName}}',
    '{{dashboardUrl}}',
  ],
  registration_approved: [
    '{{appName}}',
    '{{name}}',
    '{{companyName}}',
    '{{to}}',
    '{{loginUrl}}',
    '{{password}}',
    '{{hasPassword}}',
  ],
  password_reset: [
    '{{appName}}',
    '{{name}}',
    '{{email}}',
    '{{resetUrl}}',
    '{{expirationTime}}',
  ],
  password_reset_success: [
    '{{appName}}',
    '{{name}}',
    '{{email}}',
    '{{resetTime}}',
    '{{loginUrl}}',
  ],
};

const warningSampleData = (pct: string, used: string, limit: string): Record<string, string> => ({
  appName: 'callbert.ai',
  userName: 'Max Mustermann',
  customerName: 'Musterfirma GmbH',
  resourceName: 'Freiminuten',
  usedAmount: used,
  limitAmount: limit,
  percentage: pct,
  upgradeUrl: 'https://app.callbert.ai/settings/subscription',
});

const SAMPLE_DATA: Record<string, Record<string, string>> = {
  usage_warning_50: warningSampleData('50', '250', '500'),
  usage_warning_70: warningSampleData('70', '350', '500'),
  usage_warning_80: warningSampleData('80', '400', '500'),
  usage_warning_90: warningSampleData('90', '450', '500'),
  usage_limit_reached: {
    appName: 'callbert.ai',
    userName: 'Max Mustermann',
    customerName: 'Musterfirma GmbH',
    resourceName: 'Freiminuten',
    upgradeUrl: 'https://app.callbert.ai/settings/subscription',
  },
  admin_event_notification: {
    appName: 'callbert.ai',
    adminName: 'Admin User',
    eventLabel: 'Subscription Plan geändert',
    customerName: 'Musterfirma GmbH',
    description: 'Plan wurde von Basic auf Professional geändert.',
    eventTime: new Date().toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }),
  },
  customer_suspended: {
    appName: 'callbert.ai',
    userName: 'Max Mustermann',
    customerName: 'Musterfirma GmbH',
    reason: 'Unbezahlte Rechnung',
  },
  customer_resumed: {
    appName: 'callbert.ai',
    userName: 'Max Mustermann',
    customerName: 'Musterfirma GmbH',
  },
  phone_number_purchased: {
    appName: 'callbert.ai',
    userName: 'Max Mustermann',
    customerName: 'Musterfirma GmbH',
    phoneNumber: '+49 30 123456789',
    agentName: 'Support Bot',
    date: new Date().toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }),
  },
  user_invitation: {
    appName: 'callbert.ai',
    inviteeName: 'Anna Schmidt',
    inviterName: 'Max Mustermann',
    customerName: 'Musterfirma GmbH',
    invitationUrl: 'https://app.callbert.ai/invitation/accept/abc123',
    expiresAt: '25.02.2026, 10:00',
  },
  invitation_accepted: {
    appName: 'callbert.ai',
    inviterName: 'Max Mustermann',
    inviteeName: 'Anna Schmidt',
    inviteeEmail: 'anna@example.com',
    customerName: 'Musterfirma GmbH',
  },
  invitation_revoked: {
    appName: 'callbert.ai',
    inviteeName: 'Anna Schmidt',
    customerName: 'Musterfirma GmbH',
  },
  registration_verification: {
    appName: 'callbert.ai',
    name: 'Max Mustermann',
    companyName: 'Musterfirma GmbH',
    verificationUrl: 'https://app.callbert.ai/register/verify/abc123',
    expiresAt: '25.02.2026, 10:00',
  },
  registration_welcome: {
    appName: 'callbert.ai',
    name: 'Max Mustermann',
    companyName: 'Musterfirma GmbH',
    dashboardUrl: 'https://app.callbert.ai/dashboard',
  },
  registration_approved: {
    appName: 'callbert.ai',
    name: 'Max Mustermann',
    companyName: 'Musterfirma GmbH',
    to: 'max@musterfirma.de',
    loginUrl: 'https://app.callbert.ai/login',
    password: 'TempPass123!',
    hasPassword: 'true',
  },
  password_reset: {
    appName: 'callbert.ai',
    name: 'Max Mustermann',
    email: 'max@musterfirma.de',
    resetUrl: 'https://app.callbert.ai/reset-password/abc123',
    expirationTime: '1 Stunde',
  },
  password_reset_success: {
    appName: 'callbert.ai',
    name: 'Max Mustermann',
    email: 'max@musterfirma.de',
    resetTime: new Date().toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }),
    loginUrl: 'https://app.callbert.ai/login',
  },
};

function renderHandlebarsSimple(template: string, data: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => data[key] ?? `{{${key}}}`);
}

interface FormData {
  templateType: string;
  language: string;
  subject: string;
  body: string;
}

export function NotificationTemplatesPage() {
  const [templates, setTemplates] = useState<EventEmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EventEmailTemplate | null>(null);
  const [editingType, setEditingType] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [, setNewLanguage] = useState('');

  const [formData, setFormData] = useState<FormData>({
    templateType: '',
    language: 'de',
    subject: '',
    body: '',
  });

  const loadTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getEventEmailTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Benachrichtigungsvorlagen konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleOpenType = (type: string) => {
    setEditingType(type);
    setIsCreating(false);
    setNewLanguage('');
    const typeTemplates = templates.filter((t) => t.templateType === type);
    const deTemplate = typeTemplates.find((t) => t.language === 'de') || typeTemplates[0];
    if (deTemplate) {
      setEditingTemplate(deTemplate);
      setFormData({
        templateType: deTemplate.templateType,
        language: deTemplate.language,
        subject: deTemplate.subject,
        body: deTemplate.body,
      });
    } else {
      setEditingTemplate(null);
      setIsCreating(true);
      setFormData({ templateType: type, language: 'de', subject: '', body: '' });
    }
    setIsDialogOpen(true);
  };

  const switchLanguage = (template: EventEmailTemplate) => {
    setEditingTemplate(template);
    setIsCreating(false);
    setNewLanguage('');
    setFormData({
      templateType: template.templateType,
      language: template.language,
      subject: template.subject,
      body: template.body,
    });
  };

  const startNewLanguage = () => {
    setEditingTemplate(null);
    setIsCreating(true);
    setNewLanguage('');
    setFormData({ ...formData, language: '', subject: '', body: '' });
  };


  const handleSave = async () => {
    if (!formData.subject.trim() || !formData.body.trim()) {
      toast.error('Betreff und Inhalt sind Pflichtfelder.');
      return;
    }
    if (isCreating && !formData.language.trim()) {
      toast.error('Bitte geben Sie einen Sprachcode ein (z.B. en, fr, it).');
      return;
    }

    setIsSaving(true);
    try {
      if (isCreating) {
        const created = await apiService.createEventEmailTemplate({
          templateType: formData.templateType,
          language: formData.language,
          subject: formData.subject,
          body: formData.body,
        });
        setTemplates((prev) => [...prev, created]);
        setEditingTemplate(created);
        setIsCreating(false);
        setNewLanguage('');
        toast.success('Vorlage wurde erfolgreich erstellt.');
      } else if (editingTemplate) {
        const updated = await apiService.updateEventEmailTemplate(editingTemplate.id, {
          subject: formData.subject,
          body: formData.body,
        });
        setTemplates((prev) => prev.map((t) => (t.id === editingTemplate.id ? updated : t)));
        setEditingTemplate(updated);
        toast.success('Vorlage wurde erfolgreich aktualisiert.');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async (template: EventEmailTemplate) => {
    setIsPreviewLoading(true);
    setIsPreviewOpen(true);
    try {
      const result = await apiService.previewEventEmailTemplate({
        templateType: template.templateType,
        language: template.language,
      });
      if (result.error) {
        toast.error(result.error);
        setIsPreviewOpen(false);
      } else {
        setPreviewSubject(result.subject);
        setPreviewHtml(result.body);
      }
    } catch (error) {
      console.error('Failed to preview template:', error);
      toast.error('Vorschau konnte nicht geladen werden.');
      setIsPreviewOpen(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };


  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingTemplate(null);
    setEditingType(null);
    setIsCreating(false);
    setNewLanguage('');
    setFormData({
      templateType: '',
      language: 'de',
      subject: '',
      body: '',
    });
  };

  // Group templates by type
  const templatesByType = templates.reduce(
    (acc, t) => {
      if (!acc[t.templateType]) acc[t.templateType] = [];
      acc[t.templateType].push(t);
      return acc;
    },
    {} as Record<string, EventEmailTemplate[]>,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Benachrichtigungsvorlagen</h1>
          <p className="text-gray-600">
            E-Mail-Vorlagen fuer alle System- und Transaktions-Benachrichtigungen
          </p>
        </div>
        <Button variant="outline" onClick={loadTemplates}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Object.entries(templatesByType).map(([type, typeTemplates]) => {
          const deTemplate = typeTemplates.find((t) => t.language === 'de');
          const languages = typeTemplates.map((t) => t.language).sort();
          const missingRequired = (['de', 'en'] as const).filter(
            (lang) => !languages.includes(lang),
          );

          return (
            <Card key={type} className="flex flex-col">
              <CardContent className="px-3 pt-3 pb-2">
                <span className="text-xs font-medium truncate block">{type}</span>
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  {languages.map((lang) => (
                    <span
                      key={lang}
                      className="inline-block text-[10px] font-mono uppercase leading-none px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                    >
                      {lang}
                    </span>
                  ))}
                  {missingRequired.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] leading-none px-1.5 py-0.5 rounded bg-amber-50 text-amber-700"
                      title={`Fehlend: ${missingRequired.map((l) => l.toUpperCase()).join(', ')}`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      {missingRequired.map((l) => l.toUpperCase()).join(', ')}
                    </span>
                  )}
                </div>
              </CardContent>
              <div
                className="mx-3 mb-2 rounded border bg-white cursor-pointer"
                style={{ height: '200px', maxHeight: '200px', overflow: 'hidden', position: 'relative' }}
                onClick={() => deTemplate && handlePreview(deTemplate)}
              >
                {deTemplate ? (
                  <EmailHtmlPreview
                    html={deTemplate.body}
                    title={type}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '500%',
                      height: '1000px',
                      border: 'none',
                      transform: 'scale(0.2)',
                      transformOrigin: 'top left',
                      pointerEvents: 'none',
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50">
                    <span className="text-xs text-gray-400">Kein DE-Template</span>
                  </div>
                )}
              </div>
              <CardContent className="px-3 pt-2 pb-3 flex items-center justify-center gap-1 border-t">
                {deTemplate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreview(deTemplate)}
                    title="Vorschau (DE)"
                    className="h-7 w-7 p-0"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenType(type)}
                  title="Bearbeiten"
                  className="h-7 w-7 p-0"
                >
                  <Edit className="w-3.5 h-3.5" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{formData.templateType}</DialogTitle>
            <DialogDescription>
              {isCreating ? 'Neue Sprachversion erstellen' : 'Vorlage bearbeiten'}
            </DialogDescription>
          </DialogHeader>

          {/* Sprach-Tabs */}
          {editingType && (
            <div className="flex items-center gap-1 flex-wrap">
              {templates
                .filter((t) => t.templateType === editingType)
                .map((t) => (
                  <Button
                    key={t.id}
                    variant={!isCreating && editingTemplate?.id === t.id ? 'default' : 'outline'}
                    size="sm"
                    className="uppercase font-mono text-xs"
                    onClick={() => switchLanguage(t)}
                  >
                    {t.language}
                  </Button>
                ))}
              {isCreating ? (
                <Input
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value.toLowerCase() })}
                  placeholder="z.B. fr, it"
                  maxLength={10}
                  className="w-20 h-8 text-xs uppercase font-mono"
                  autoFocus
                />
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={startNewLanguage}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Sprache
                </Button>
              )}
            </div>
          )}

          <div className="flex gap-4 flex-1 min-h-0">
            {/* Linke Seite: Editor */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="space-y-2 shrink-0">
                <Label htmlFor="subject">Betreff (Handlebars-Template)</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="z.B. Verbrauchswarnung: {{percentage}}% verbraucht"
                />
              </div>

              <div className="flex flex-col min-h-0 flex-1 mt-4">
                <Label htmlFor="body" className="mb-2 shrink-0">HTML-Body (Handlebars-Template)</Label>
                <Textarea
                  id="body"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="HTML-Template mit Handlebars-Variablen..."
                  className="font-mono text-xs flex-1 resize-none overflow-y-auto"
                />
              </div>

              {/* Variable help */}
              {formData.templateType && TEMPLATE_VARIABLES[formData.templateType] && (
                <div className="p-3 bg-gray-50 rounded-lg mt-4 shrink-0">
                  <p className="text-xs font-medium text-gray-700 mb-2">Verfügbare Variablen:</p>
                  <div className="flex flex-wrap gap-1">
                    {TEMPLATE_VARIABLES[formData.templateType].map((v) => (
                      <code
                        key={v}
                        className="text-xs bg-white border px-1.5 py-0.5 rounded cursor-pointer hover:bg-blue-50"
                        onClick={() => {
                          void copyToClipboard(v, v);
                        }}
                        title="Klicken zum Kopieren"
                      >
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2 mt-4 shrink-0">
                <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {isCreating ? 'Erstellen' : 'Speichern'}
                </Button>
              </div>
            </div>

            {/* Rechte Seite: Live-Vorschau */}
            <div className="flex-1 border rounded-lg overflow-hidden flex flex-col min-h-0 bg-gray-50">
              <div className="px-3 py-2 border-b bg-white text-xs font-medium text-gray-500 shrink-0">
                Live-Vorschau
              </div>
              {formData.body ? (
                <EmailHtmlPreview
                  html={renderHandlebarsSimple(formData.body, SAMPLE_DATA[formData.templateType] || {})}
                  title="Live-Vorschau"
                  className="flex-1 w-full border-0 bg-white"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                  Keine Vorschau verfügbar
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ zIndex: 100 }}>
          <DialogHeader>
            <DialogTitle>Vorschau</DialogTitle>
            {previewSubject && (
              <DialogDescription>
                Betreff: {previewSubject}
              </DialogDescription>
            )}
          </DialogHeader>
          {isPreviewLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : previewHtml ? (
            <EmailHtmlPreview
              html={previewHtml}
              title="Vorschau"
              className="w-full rounded-lg border bg-white"
              style={{ height: '70vh' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
